/**
 * Maps: Maplist anzeigen/bearbeiten (mapList.*), Rundensteuerung (Next/Restart)
 * sowie die Gameplay-Steuerung Tickets (set/×2) und Flag-by-Flag — von der
 * Players-View hierher verschoben. RCON-/§4-Aktionen unverändert.
 *
 * Der frühere "TACTICAL MAP CONFIG"-Altpfad (Manual TAC Objects / Object Registry
 * / Calibration-Anchor) wurde entfernt — TAC-Kalibrierung läuft jetzt direkt im
 * Players Tactical Map Inline-Editor (Fit/Objects/Crop/Calibrate + großes Overlay).
 * Die `tac_maps` / `tac_calib_save` API bleibt (vom Inline-Editor genutzt).
 */

import { useSignal, useSignalEffect } from "@preact/signals";
import { api, activeServer } from "../store";
import { PageHead, Panel, NoServer } from "../components";
import { PanelGrid, type PanelItem } from "../grid/PanelGrid";
import { toast } from "../toast";
import type { ApiResponse } from "../api";
import { formatMapName, modeLabel } from "../parse";
import { BF2142_MAPS, GAME_MODES, defaultSize, isRconMethodError, mapOptions, modeSizes, parseAvailableMaps, sizeLabel } from "../../maps/constants";
import { t } from "../i18n";

/** Legacy "Load now": sofortiger Mapwechsel (beendet die Runde). Wie app.js loadMapNow. */
export function changemapCommand(map: string, mode: string, size: string): string {
  return `admin.changemap ${map.trim()} ${mode} ${size}`;
}

/** Ein Maplist-Eintrag (eine Rotation-Zeile). */
export interface MaplistEntry {
  index: number;
  map: string;
  mode: string;
  size: number;
}

/**
 * Rohausgabe von `mapList.list` in Einträge parsen. Zeilen wie
 * `0: "camp_gibraltar" gpm_nv 16` (Quotes optional). Nicht passende/leere
 * Zeilen (Junk) werden ignoriert.
 */
export function parseMaplist(raw: string | null | undefined): MaplistEntry[] {
  const out: MaplistEntry[] = [];
  for (const line of String(raw ?? "").split(/\r?\n/)) {
    const m = line.match(/^\s*(\d+)\s*:\s*"?([^"\s]+)"?\s+(\S+)\s+(\d+)/);
    if (!m) continue;
    out.push({ index: Number(m[1]), map: m[2]!, mode: m[3]!, size: Number(m[4]) });
  }
  return out;
}

/**
 * Entfernt den Eintrag mit `index` (Rotation-Position) und nummeriert die
 * restlichen Indizes lückenlos neu. Duplikate (gleiche Map, anderer Index)
 * bleiben unberührt — es wird genau die gewählte Position entfernt. Leere/
 * unbekannte Eingabe → unveränderte (renummerierte) Kopie, kein Crash.
 */
export function removeMaplistEntry(entries: MaplistEntry[], index: number): MaplistEntry[] {
  const pos = entries.findIndex((e) => e.index === index);
  const kept = pos >= 0 ? entries.filter((_, i) => i !== pos) : entries.slice();
  return kept.map((e, i) => ({ ...e, index: i }));
}

export function Maps() {
  const raw = useSignal("");
  const entries = useSignal<MaplistEntry[]>([]);
  const map = useSignal<string>(BF2142_MAPS[0] ?? "");
  const mode = useSignal("gpm_cq");
  const size = useSignal(String(defaultSize("gpm_cq")));
  const idx = useSignal("");
  const mapInfo = useSignal("");
  const availableMaps = useSignal<string[]>([...BF2142_MAPS]);
  const tickets = useSignal<{ t1: number; t2: number } | null>(null);
  const fbf = useSignal<{ on: boolean; reapply: boolean } | null>(null);
  const tpac = useSignal("");
  const teu = useSignal("");
  const edit = useSignal(false);

  /** Karten-Befehl über die eigene Aktion `maps_cmd`. null = Fehler (Toast schon gezeigt). */
  async function exec(cmd: string): Promise<string | null> {
    const sv = activeServer.value;
    if (!sv) return null;
    const r = await api.call<ApiResponse & { raw: string }>("maps_cmd", { server: sv, cmd });
    if (!r.ok) { toast(String(r.error ?? t("common.error")), "err"); return null; }
    return r.raw ?? "";
  }
  /** Server-Antwort anzeigen, falls vorhanden (z.B. Fehlertext beim Mapwechsel). */
  function showResponse(cmd: string, out: string) {
    mapInfo.value = out.trim() ? `${cmd} → ${out.trim()}` : "";
  }
  async function loadList() {
    const out = await exec("mapList.list");
    if (out === null) return;
    raw.value = out;
    entries.value = parseMaplist(out);
  }
  async function loadGameplay() {
    const sv = activeServer.value;
    if (!sv) return;
    const [st, fb] = await Promise.allSettled([
      api.call<ApiResponse & { status: Record<string, unknown> | null }>("server_status", { server: sv }),
      api.call<ApiResponse & { fbf: { on: boolean; reapply: boolean } }>("fbf_get", { server: sv }),
    ]);
    if (st.status === "fulfilled" && st.value.ok && st.value.status) {
      tickets.value = { t1: Number(st.value.status.t1) || 0, t2: Number(st.value.status.t2) || 0 };
    }
    if (fb.status === "fulfilled" && fb.value.ok) fbf.value = fb.value.fbf;
  }
  useSignalEffect(() => {
    if (activeServer.value) {
      void loadList();
      void loadGameplay();
      void loadAvailableMaps();
    }
  });

  // Live-Mapliste vom Server (best-effort, leise) — sonst bleibt BF2142_MAPS.
  // `mapList.listAll` ist nicht auf allen Servern verfügbar (z.B. afex-suez-gunship:
  // „Unknown object or method!"). In dem Fall liefert parseAvailableMaps null, das
  // Dropdown bleibt auf der statischen BF2142_MAPS-Liste, die Maplist-Anzeige nutzt
  // weiter mapList.list (loadList). Kein harter Fehler, kein Rohtext als Option.
  async function loadAvailableMaps() {
    const sv = activeServer.value;
    if (!sv) return;
    try {
      const r = await api.call<ApiResponse & { raw: string }>("maps_cmd", { server: sv, cmd: "mapList.listAll" });
      const raw = r.ok ? r.raw : null;
      const parsed = parseAvailableMaps(raw);
      if (parsed) {
        availableMaps.value = parsed;
        if (!parsed.includes(map.value)) map.value = parsed[0] ?? map.value;
      } else if (raw != null && isRconMethodError(raw)) {
        // Optionaler interner Hinweis (debug, KEIN Error/Warn → keine Console-Errors).
        console.debug("mapList.listAll unsupported, using fallback");
      }
    } catch { /* Fallback BF2142_MAPS bleibt */ }
  }

  // Moduswechsel: ungültige Größe auf den Modus-Default zurücksetzen.
  function selectMode(id: string) {
    mode.value = id;
    if (!modeSizes(id).includes(Number(size.value))) size.value = String(defaultSize(id));
  }

  async function quick(cmd: string, msg: string) {
    const out = await exec(cmd);
    if (out === null) return;
    showResponse(cmd, out);
    toast(msg, "ok");
  }
  async function append() {
    const m = map.value.trim();
    if (!m) return toast(t("maps.toastPickMap"), "err");
    // Auswahl bewusst NICHT leeren (Select zeigt sonst eine Map, der Zustand
    // wäre aber leer -> nächster Klick täte nichts). Wie Legacy addMap().
    const out = await exec(`mapList.append ${m} ${mode.value} ${size.value}`);
    if (out === null) return;
    showResponse("mapList.append", out);
    toast(t("maps.toastMapAdded"), "ok");
    await loadList();
  }
  // Entfernen wie Anhängen: direkte (In-Memory-)Server-Mutation + autoritativer
  // Reload. Lokal sofort renummeriert für Feedback; mapList.save persistiert.
  async function removeEntry(index: number) {
    entries.value = removeMaplistEntry(entries.value, index);
    const out = await exec(`mapList.remove ${index}`);
    if (out !== null) toast(t("maps.toastRemoved"), "ok");
    await loadList(); // autoritativ (setzt die lokale Vorschau bei Fehler zurück)
  }
  async function setIndex() {
    if (idx.value === "") return;
    const out = await exec(`mapList.currentMap ${parseInt(idx.value, 10) || 0}`);
    if (out === null) return;
    showResponse("mapList.currentMap", out);
    toast(`${t("maps.nextMapIndex")} ${idx.value}`, "ok");
  }
  async function showCurrentMap() {
    const r = await exec("admin.currentLevel");
    if (r !== null) mapInfo.value = `Current Map ID: ${r.trim() || "—"}`;
  }
  async function showNextMap() {
    const r = await exec("admin.nextLevel");
    if (r !== null) mapInfo.value = `Next Map ID: ${r.trim() || "—"}`;
  }
  async function loadMapNow() {
    const m = map.value.trim();
    if (!m) return toast(t("maps.toastPickMap"), "err");
    if (!confirm(`${t("maps.switchTo")} "${m}" (${mode.value}, ${size.value})?\n${t("maps.roundEnds")}`)) return;
    const out = await exec(changemapCommand(m, mode.value, size.value));
    if (out === null) return;
    showResponse("admin.changemap", out);
    toast(`${t("maps.switching")} ${m}…`, "ok");
  }
  async function applyTickets() {
    const sv = activeServer.value;
    if (!sv) return;
    const r = await api.call<ApiResponse & { t1: number; t2: number }>("tickets_set", { server: sv, pac: parseInt(tpac.value || "0", 10), eu: parseInt(teu.value || "0", 10) });
    if (r.ok) { tickets.value = { t1: r.t1, t2: r.t2 }; toast(t("maps.toastTicketsSet"), "ok"); } else toast(String(r.error ?? t("common.error")), "err");
  }
  async function doubleTickets() {
    const sv = activeServer.value;
    if (!sv) return;
    const r = await api.call<ApiResponse & { t1: number; t2: number }>("tickets_x2", { server: sv });
    if (r.ok) { tickets.value = { t1: r.t1, t2: r.t2 }; toast(t("maps.toastTicketsDoubled"), "ok"); } else toast(String(r.error ?? t("common.error")), "err");
  }
  async function toggleFbf() {
    const sv = activeServer.value;
    if (!sv) return;
    const on = !(fbf.value?.on ?? false);
    const r = await api.call<ApiResponse & { fbf: { on: boolean; reapply: boolean } }>("fbf_set", { server: sv, on });
    if (r.ok) { fbf.value = r.fbf; toast(on ? t("maps.fbfEnabled") : t("maps.fbfDisabled"), "ok"); } else toast(String(r.error ?? t("common.error")), "err");
  }

  if (!activeServer.value) return (<><PageHead title={t("view.maps")} /><NoServer /></>);

  // Default-Layout = aktuelles Aussehen: Maplist breit (⅔), Steuer-Panels (⅓).
  const items: PanelItem[] = [
    { id: "maplist", title: t("maps.currentMaplist"), defaultSpan: 8, render: () => (
      <Panel title={t("maps.currentMaplist")} actions={
        <>
          <span class="badge">{entries.value.length} Maps</span>
          <button class="btn sm" onClick={() => void loadList()}>↻ {t("common.load")}</button>
          <button class="btn sm" onClick={() => void quick("mapList.save", t("maps.toastSaved"))}>💾 {t("common.save")}</button>
        </>
      }>
        {entries.value.length === 0 ? (
          <div class="empty">{t("maps.emptyRotation")}</div>
        ) : (
          <div style="max-height:52vh;overflow:auto">
            <table class="score">
              <thead><tr><th class="num">#</th><th>{t("common.map")}</th><th>{t("common.mode")}</th><th class="num">{t("common.size")}</th><th class="num actions-head">{t("field.action")}</th></tr></thead>
              <tbody>
                {entries.value.map((e) => (
                  <tr key={`${e.index}-${e.map}-${e.mode}-${e.size}`}>
                    <td class="num mono faint">{e.index}</td>
                    <td>{formatMapName(e.map)}</td>
                    <td>{modeLabel(e.mode)} <span class="mono faint">({e.mode})</span></td>
                    <td class="num mono">{e.size}</td>
                    <td class="num"><button class="btn sm danger" title={`${t("maps.removeEntry")} ${e.index}`} onClick={() => void removeEntry(e.index)}>{t("common.remove")}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <details style="margin-top:var(--s3)">
          <summary class="caption" style="cursor:pointer">{t("maps.rawOutput")} (mapList.list)</summary>
          <pre class="mono" style="white-space:pre-wrap;font-size:12px;margin:var(--s2) 0 0;max-height:30vh;overflow:auto">{raw.value || "—"}</pre>
        </details>
      </Panel>
    ) },
    { id: "tickets", title: "Tickets", defaultSpan: 4, render: () => (
      <Panel title="Tickets">
        <div class="stack">
          <div class="row" style="gap:var(--s3)">
            <div class="field grow"><label>PAC</label><input class="input mono" inputMode="numeric" placeholder={String(tickets.value?.t1 ?? "")} value={tpac.value} onInput={(e) => (tpac.value = (e.target as HTMLInputElement).value)} /></div>
            <div class="field grow"><label>EU</label><input class="input mono" inputMode="numeric" placeholder={String(tickets.value?.t2 ?? "")} value={teu.value} onInput={(e) => (teu.value = (e.target as HTMLInputElement).value)} /></div>
          </div>
          <div class="row">
            <button class="btn primary grow" onClick={() => void applyTickets()}>{t("common.apply")}</button>
            <button class="btn" onClick={() => void doubleTickets()} title={t("maps.doubleTitle")}>×2</button>
          </div>
        </div>
      </Panel>
    ) },
    { id: "fbf", title: "Flag-by-Flag", defaultSpan: 4, render: () => (
      <Panel title="Flag-by-Flag" actions={<span class={`badge ${fbf.value?.on ? "accent" : ""}`}>{fbf.value?.on ? t("status.on") : t("status.off")}</span>}>
        <button class={`btn ${fbf.value?.on ? "" : "primary"}`} style="width:100%" onClick={() => void toggleFbf()}>
          {fbf.value?.on ? t("maps.fbfOff") : t("maps.fbfOn")}
        </button>
      </Panel>
    ) },
    { id: "mapadd", title: t("maps.addMap"), defaultSpan: 4, render: () => (
      <Panel title={t("maps.addMap")}>
        <div class="stack">
          <div class="field"><label>{t("common.map")}</label>
            <select class="select mono" value={map.value} onChange={(e) => (map.value = (e.target as HTMLSelectElement).value)}>
              {mapOptions(availableMaps.value).map((m) => <option key={m.slug} value={m.slug}>{m.label}</option>)}
            </select>
          </div>
          <div class="row">
            <div class="field grow"><label>{t("common.mode")}</label>
              <select class="select" value={mode.value} onChange={(e) => selectMode((e.target as HTMLSelectElement).value)}>
                {GAME_MODES.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div class="field grow"><label>{t("common.size")}</label>
              <select class="select" value={size.value} onChange={(e) => (size.value = (e.target as HTMLSelectElement).value)}>
                {modeSizes(mode.value).map((s) => <option key={s} value={String(s)}>{sizeLabel(s)}</option>)}
              </select>
            </div>
          </div>
          <div class="row">
            <button class="btn primary grow" onClick={() => void append()}>+ {t("maps.append")}</button>
            <button class="btn" onClick={() => void quick("mapList.save", t("maps.toastSaved"))}>💾 {t("common.save")}</button>
            <button class="btn" onClick={() => void loadMapNow()} title={t("maps.loadNowTitle")}>▶ {t("maps.loadNow")}</button>
          </div>
          {mapInfo.value && <div class="caption mono">{mapInfo.value}</div>}
        </div>
      </Panel>
    ) },
    { id: "nextmap", title: t("maps.setNextMap"), defaultSpan: 4, render: () => (
      <Panel title={t("maps.setNextMap")}>
        <div class="stack">
          <div class="row">
            <input class="input mono grow" inputMode="numeric" placeholder="Index" value={idx.value} onInput={(e) => (idx.value = (e.target as HTMLInputElement).value)} />
            <button class="btn" onClick={() => void setIndex()}>{t("common.set")}</button>
          </div>
          <div class="row">
            <button class="btn grow" onClick={() => void showCurrentMap()}>Current Map ID</button>
            <button class="btn grow" onClick={() => void showNextMap()}>Next Map ID</button>
          </div>
          {mapInfo.value && <div class="caption mono">{mapInfo.value}</div>}
        </div>
      </Panel>
    ) },
  ];

  return (
    <div class="view-enter">
      <PageHead title={t("view.maps")} sub={t("maps.sub")}>
        <button class={`btn sm ${edit.value ? "primary" : ""}`} onClick={() => (edit.value = !edit.value)}>{edit.value ? t("dashboard.editDone") : t("dashboard.edit")}</button>
        <button class="btn sm" onClick={() => void quick("admin.restartMap", t("maps.toastRestarted"))}>↺ Restart</button>
        <button class="btn primary sm" onClick={() => void quick("admin.runNextLevel", t("maps.toastNextLoaded"))}>▶ {t("maps.nextMap")}</button>
      </PageHead>
      <PanelGrid view="maps" items={items} edit={edit} />
    </div>
  );
}
